import { DeliveryMethod, useShoppingCart } from "../context/ShoppingCartContext";
import { format } from "date-fns";
import etLocale from "date-fns/locale/et";
import enLocale from "date-fns/locale/en-GB";
import CheckoutDeliveryMethodButton from "./CheckoutDeliveryMethodButton";
import WestIcon from "@mui/icons-material/West";
import PinDropIcon from "@mui/icons-material/PinDrop";
import PersonIcon from "@mui/icons-material/Person";
import { LocalShipping } from "@mui/icons-material";
import { pf } from "../utilities/helpers";
import usePageLanguage from "../hooks/usePageLanguage";
import React from "react";
import {
  filterDeliveryMethodsForConfig,
  getSyncOrderCheckoutContext,
  useCheckoutConfig,
} from "./checkoutConfig";

interface Props {
  setModalDeliveryMethod: (val: DeliveryMethod | null) => void;
  setShowDeliveryModal: (val: boolean) => void;
  readOnly?: boolean;
}

const languageTranslations = {
  et: {
    delivery: "TARNE",
    back_to_shipping: "Tagasi tarneviisi valikusse",
    edit: "Muuda",
  },
  en: {
    delivery: "DELIVERY",
    back_to_shipping: "Back to shipping method selection",
    edit: "Edit",
  },
};

export default function CheckoutDeliveryPanel({
  setModalDeliveryMethod,
  setShowDeliveryModal,
  readOnly = false,
}: Props) {
  const {
    order,
    deliveryMethods,
    deliveryConfirmed,
    syncOrder,
    buyer,
  } = useShoppingCart();
  const checkoutConfig = useCheckoutConfig();
  const checkoutContext = getSyncOrderCheckoutContext(checkoutConfig.delivery);
  const visibleDeliveryMethods = filterDeliveryMethodsForConfig(
    deliveryMethods,
    checkoutConfig.delivery
  );

  const locale = usePageLanguage();

  const selectedDelivery = readOnly
    ? deliveryConfirmed ?? order?.delivery ?? null
    : order?.delivery ?? null;
  const deliveryMethod =
    selectedDelivery?.uuid != null
      ? (visibleDeliveryMethods.find((i) => i.uuid === selectedDelivery.uuid) ??
          null)
      : null;

  const isValidDate = (dateString: string): boolean => {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  const receiverEmail = selectedDelivery?.receiver?.email ?? buyer.email ?? "";
  const receiverFirstname =
    selectedDelivery?.receiver?.firstname ?? buyer.firstname ?? "";
  const receiverSurname =
    selectedDelivery?.receiver?.surname ?? buyer.surname ?? "";
  const receiverPhone = selectedDelivery?.receiver?.phone ?? buyer.phone ?? "";
  const address =
    selectedDelivery?.receiver?.address ?? "";
  const city = selectedDelivery?.receiver?.city ?? "";
  const postalCode =
    selectedDelivery?.receiver?.postalCode ?? "";
  const parcelMachineName = selectedDelivery?.parcelMachineName ?? "";
  const deliveryTime =
    selectedDelivery?.requestedDeliveryDateTime ??
    selectedDelivery?.estimatedDeliveryDateTime;
  const isPickup = selectedDelivery?.methodCode === "store_pickup";

  if (readOnly && selectedDelivery && deliveryMethod) {
    return (
      <div className="panel" id="checkoutDeliveryPanel">
        <div className="panel-heading">
          <div className="panel-title">
            <span className="badge badge-square badge-primary ">3</span>
            <span className="panel-title-text">
              {languageTranslations[locale]?.delivery}
            </span>
            {deliveryTime != undefined && (
              <span className="pull-right badge badge-square badge-primary delivery-date-badge">
                <span style={{ textTransform: "capitalize" }}>
                  {locale === "et"
                    ? format(new Date(deliveryTime), "EEEE", {
                        locale: etLocale,
                      })
                    : locale === "en"
                    ? format(new Date(deliveryTime), "EEEE", {
                        locale: enLocale,
                      })
                    : locale === "ru"
                    ? "Russian Translation"
                    : "Default Translation"}
                </span>{" "}
                {format(new Date(deliveryTime), "dd.MM.yyyy")}
              </span>
            )}
          </div>
        </div>
        <div className="panel-body">
          <div className="row" id="deliveryMethodFilled">
            <div className="col-xs-12 margin-bottom-10">
              <button
                className="btn btn-primary-outline text-left checkoutDeliveryButton"
                style={{ width: "100%" }}
                type="button"
              >
                  {deliveryMethod?.translations?.[locale]?.name}
                <span className="pull-right">
                  {" "}
                  {pf(Number(selectedDelivery.taxInclusivePriceAmount ?? 0))} &euro;
                </span>
              </button>
            </div>
            <div className="col-xs-12">
              {!isPickup && (
                <h3 className="addressInfo margin-top-15 margin-bottom-0">
                  <PinDropIcon className="icon margin-right-10" />
                  <span>
                    <span className="receiverAddress">
                      {parcelMachineName ? parcelMachineName + " (" : ""}
                      {address}
                      {city.length ? ", " + city : ""}
                      {postalCode.length ? ", " + postalCode : ""}
                      {parcelMachineName ? ")" : ""}
                    </span>
                  </span>
                </h3>
              )}
              {receiverEmail && receiverEmail.length != 0 && (
                  <h3
                    className="receiverInfo margin-top-10 margin-bottom-0 "
                    style={{ fontSize: "16px", fontWeight: "400" }}
                  >
                    <PersonIcon className="icon" />
                    {receiverFirstname && receiverSurname && (
                      <span
                        className="receiverName"
                        style={{ fontSize: "16px", fontWeight: "400" }}
                      >
                        {receiverFirstname + " " + receiverSurname}
                      </span>
                    )}
                    {receiverFirstname &&
                      receiverSurname &&
                      (receiverPhone || receiverEmail) &&
                      ", "}
                    {receiverPhone && (
                      <span
                        className="receiverPhone"
                        style={{ fontSize: "16px", fontWeight: "400" }}
                      >
                        {receiverPhone}
                      </span>
                    )}
                    {receiverPhone && receiverEmail && ", "}
                    {receiverEmail && (
                      <span
                        className="receiverEmail"
                        style={{ fontSize: "16px", fontWeight: "400" }}
                      >
                        {receiverEmail}
                      </span>
                    )}
                  </h3>
                )}

              {selectedDelivery?.requestedDeliveryDateTime &&
                isValidDate(selectedDelivery.requestedDeliveryDateTime) && (
                  <h3 className="receiverInfo margin-top-10 margin-bottom-0 ">
                    <LocalShipping className="icon" />
                    <span
                      className="receiverName"
                      style={{ marginLeft: "5px" }}
                    >
                      {format(
                        new Date(selectedDelivery.requestedDeliveryDateTime),
                        "EEEE, dd.MM.yyyy, HH:mm",
                        {
                          locale: locale === "et" ? etLocale : enLocale,
                        }
                      )}
                    </span>
                  </h3>
                )}
            </div>
            {selectedDelivery &&
            (deliveryMethod?.translations?.[locale]?.description ||
              selectedDelivery.parcelDescription) ? (
              <div className="col-xs-12 deliveryDescription margin-top-15">
                {selectedDelivery.parcelDescription ? (
                  <p>{selectedDelivery.parcelDescription}</p>
                ) : (
                  ""
                )}
                {deliveryMethod?.translations?.[locale]?.description}
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" id="checkoutDeliveryPanel">
      <div className="panel-heading">
        <div className="panel-title">
          <span className="badge badge-square badge-primary">3</span>
          <span className="panel-title-text">
            {languageTranslations[locale]?.delivery}
          </span>

          {deliveryTime && !isPickup && (
              <span className="pull-right badge badge-square badge-primary delivery-date-badge">
                <span style={{ textTransform: "capitalize" }}>
                  {locale === "et"
                    ? format(new Date(deliveryTime), "EEEE", {
                        locale: etLocale,
                      })
                    : locale === "en"
                    ? format(new Date(deliveryTime), "EEEE", {
                        locale: enLocale,
                      })
                    : locale === "ru"
                    ? "Russian Translation"
                    : "Default Translation"}
                </span>{" "}
                {format(new Date(deliveryTime), "dd.MM.yyyy")}
              </span>
            )}
        </div>
      </div>
      <div className="panel-body">
        {!selectedDelivery || !deliveryMethod ? (
          <div className="row deliveryMethods">
            {visibleDeliveryMethods
              ? visibleDeliveryMethods.map((dm) => (
                  <CheckoutDeliveryMethodButton
                    key={dm.uuid}
                    deliveryMethods={visibleDeliveryMethods}
                    deliveryMethod={dm}
                    setShowDeliveryModal={setShowDeliveryModal}
                    setModalDeliveryMethod={setModalDeliveryMethod}
                  />
                ))
              : ""}
          </div>
        ) : (
          <div className="row" id="deliveryMethodFilled">
            <div className="col-xs-12">
              <button
                className="btn btn-pure backToDeliveryMethodSelect"
                onClick={() => {
                  syncOrder({
                    delivery: null,
                    checkoutContext,
                    includeB2bContactPerson:
                      checkoutConfig.b2bContactPerson.visible,
                  });
                }}
              >
                <WestIcon className="icon" style={{ fontSize: "14px" }} />{" "}
                <span>{languageTranslations[locale]?.back_to_shipping}</span>
              </button>
            </div>
            <CheckoutDeliveryMethodButton
              deliveryMethods={visibleDeliveryMethods}
              key={deliveryMethod.uuid}
              deliveryMethod={deliveryMethod}
              setShowDeliveryModal={setShowDeliveryModal}
              setModalDeliveryMethod={setModalDeliveryMethod}
            />

            <div className="col-xs-12">
              {!isPickup && (
                <h3 className="addressInfo margin-top-15 margin-bottom-0">
                  <PinDropIcon className="icon margin-right-10" />
                  <span>
                    <span className="receiverAddress">
                      {parcelMachineName ? parcelMachineName + " (" : ""}
                      {address}
                      {city.length ? ", " + city : ""}
                      {postalCode.length ? ", " + postalCode : ""}
                      {parcelMachineName ? ")" : ""}
                    </span>
                    <span
                      className="clickable editAddressButton"
                      onClick={() => {
                        setModalDeliveryMethod(deliveryMethod);
                        setShowDeliveryModal(true);
                      }}
                      style={{
                        color: "var(--shop-theme-color)",
                        fontWeight: "400",
                        marginLeft: "5px",
                        fontSize: "14px",
                      }}
                    >
                      {languageTranslations[locale]?.edit}
                    </span>
                  </span>
                </h3>
              )}
              {receiverEmail && receiverEmail.length != 0 && (
                  <h3
                    className="receiverInfo margin-top-10 margin-bottom-0 "
                    style={{ fontSize: "16px", fontWeight: "400" }}
                  >
                    <PersonIcon className="icon" />
                    {receiverFirstname && receiverSurname && (
                      <span
                        className="receiverName"
                        style={{ fontSize: "16px", fontWeight: "400" }}
                      >
                        {receiverFirstname + " " + receiverSurname}
                      </span>
                    )}
                    {receiverFirstname &&
                      receiverSurname &&
                      (receiverPhone || receiverEmail) &&
                      ", "}
                    {receiverPhone && (
                      <span
                        className="receiverPhone"
                        style={{ fontSize: "16px", fontWeight: "400" }}
                      >
                        {receiverPhone}
                      </span>
                    )}
                    {receiverPhone && receiverEmail && ", "}
                    {receiverEmail && (
                      <span
                        className="receiverEmail"
                        style={{ fontSize: "16px", fontWeight: "400" }}
                      >
                        {receiverEmail}
                      </span>
                    )}
                  </h3>
                )}

              {selectedDelivery?.requestedDeliveryDateTime &&
                isValidDate(selectedDelivery.requestedDeliveryDateTime) && (
                  <h3 className="receiverInfo margin-top-10 margin-bottom-0 ">
                    <LocalShipping className="icon" />
                    <span
                      className="receiverName"
                      style={{
                        marginLeft: "5px",
                        fontSize: "16px",
                        fontWeight: "400",
                      }}
                    >
                      {format(
                        new Date(selectedDelivery.requestedDeliveryDateTime),
                        "EEEE, dd.MM.yyyy, HH:mm",
                        {
                          locale: locale === "et" ? etLocale : enLocale,
                        }
                      )}
                    </span>
                  </h3>
                )}
            </div>
            {selectedDelivery &&
            (deliveryMethod?.translations?.[locale]?.description ||
              selectedDelivery.parcelDescription) ? (
              <div className="col-xs-12 deliveryDescription margin-top-15">
                {selectedDelivery.parcelDescription ? (
                  <>{selectedDelivery.parcelDescription}</>
                ) : (
                  ""
                )}

                {deliveryMethod?.translations?.[locale]?.description}
              </div>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
    </div>
  );
}
